import { scaleOrdinal } from 'd3-scale';
import getEntityKey from 'helpers/getEntityKey';
import { base } from 'styles/themes/colors';
import getColorSourceOfStreams from './getColorSourceOfStreams';
import { ICustomer } from 'redux/reducers/customers';
import { IIterator } from 'types/shared';

/**
 * Construct a d3-scale ordinal scale to match colors to entity keys.
 *
 * @method getColorScale
 * @param  trends The array of trends
 * @param  defaultColors Colors to use in compare mode and when no filters are applied
 * @param  customerColors customer colors
 * @param  fallbackCustomerColors Is a customer filter applied to the current trends?
 * @param  compareMode The compare mode.
 */
function getColorScale(
  trends: any[],
  defaultColors: string[],
  customerColors?: IIterator,
  fallbackCustomerColors?: ICustomer[],
  compareMode?: string | string[],
  useDefaultColors?: boolean,
  defaultSoS?: boolean
): any | undefined {
  // If there aren't any trends, we cannot assign colors to them.
  if (!trends) return;

  // Create an empty colors variable.
  let colors;

  // If we are in entity or country compare mode, we should always use the default colors.
  if (compareMode) {
    colors = defaultColors;
  } else {
    colors = trends.map(trendToCustomerColor);
  }

  const scale = scaleOrdinal(colors).domain(
    trends.map((trend: any) => getEntityKey(trend))
  );

  return function getColor(d: string): string {
    const val = typeof d === 'string' ? d : getEntityKey(d);
    return scale(val) as string;
  };

  /**
   * This function maps trends to colors
   * @param {object} trend Trend metadata
   * @param {idx} idx Index of this trend
   */
  function trendToCustomerColor(trend: any, idx: number): string {
    if (useDefaultColors) {
      return defaultColors[idx];
    }
    // Variable definition
    let color;

    // if we have the customer colors
    if (customerColors) {
      // if the trend has a 'breakdown' property
      if (trend.hasOwnProperty('breakdown')) {
        // find the matching color
        // eslint-disable-next-line no-unused-vars
        for (const customer in customerColors) {
          if (customer === trend.breakdown) {
            color = customerColors[customer];
          }
        }
      }
    }

    // if color wasn't set, and we have the fallback colors
    if (!color && fallbackCustomerColors) {
      // if the trend has a 'breakdownCustomerId' property
      if (trend.hasOwnProperty('breakdownCustomerId')) {
        // find the matching fallback color
        // eslint-disable-next-line no-unused-vars
        for (const customer of fallbackCustomerColors) {
          if (customer.id === trend?.breakdownCustomerId) {
            color = customer.color;
          }
        }
      } else if (trend.hasOwnProperty('breakdown')) {
        // find the matching fallback color
        // eslint-disable-next-line no-unused-vars
        for (const customer of fallbackCustomerColors) {
          if (customer.name === trend.breakdown) {
            color = customer.color;
          }
        }
      }
    }

    if (trend.breakdown === 'na') return base.CasperGrey;

    if (defaultSoS) {
      return getColorSourceOfStreams(trend.breakdown);
    }

    // if no color wasn't assigned still, use the default colors.
    if (!color) {
      return defaultColors[idx];
    }

    return color;
  }
}

export default getColorScale;
